'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { Package, CheckCircle2, AlertTriangle, ArrowLeft } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  useVerifyCountMutation,
  useReportDiscrepancyMutation
} from '../services/reception.mutations';
import type { ReceptionSession } from '../types';

interface StepBoxCountProps {
  session: ReceptionSession;
  expectedBoxCount: number;
  shipmentNumber: string;
  onComplete: () => void;
  onBack: () => void;
  onSessionUpdate?: (session: ReceptionSession) => void;
}

export function StepBoxCount({
  session,
  expectedBoxCount,
  shipmentNumber,
  onComplete,
  onBack,
  onSessionUpdate
}: StepBoxCountProps) {
  const t = useTranslations('reception');
  const [boxCount, setBoxCount] = useState<string>(
    session.actual_box_count != null ? String(session.actual_box_count) : ''
  );
  const [discrepancyNotes, setDiscrepancyNotes] = useState(
    session.discrepancy_notes ?? ''
  );
  const [countVerified, setCountVerified] = useState(
    session.count_status !== null
  );
  const [countStatus, setCountStatus] = useState<
    'matched' | 'mismatched' | null
  >(session.count_status);
  const [discrepancyReported, setDiscrepancyReported] = useState(
    session.count_status === 'mismatched' && !!session.discrepancy_notes
  );

  const verifyCountMutation = useVerifyCountMutation();
  const reportDiscrepancyMutation = useReportDiscrepancyMutation();

  const expectedBoxes = expectedBoxCount;

  const handleVerify = async () => {
    if (countStatus === 'matched') return;
    const actualCount = parseInt(boxCount, 10);
    if (isNaN(actualCount) || actualCount < 0) return;

    const result = await verifyCountMutation.mutateAsync({
      sessionId: session.id,
      data: { actual_box_count: actualCount }
    });

    const updatedSession = result?.data;
    setCountStatus(updatedSession?.count_status ?? null);
    setCountVerified(true);

    if (updatedSession && onSessionUpdate) {
      onSessionUpdate(updatedSession);
    }
  };

  const handleReportDiscrepancy = async () => {
    const actualCount = parseInt(boxCount, 10);
    if (isNaN(actualCount) || actualCount < 0) return;

    await reportDiscrepancyMutation.mutateAsync({
      sessionId: session.id,
      data: {
        actual_box_count: actualCount,
        notes: discrepancyNotes || null
      }
    });

    setDiscrepancyReported(true);
  };

  const canContinue =
    countVerified && (countStatus === 'matched' || discrepancyReported);

  return (
    <div className='container space-y-6'>
      <Card>
        <CardHeader>
          <CardTitle className='flex items-center gap-2'>
            <Package className='h-5 w-5' />
            {t('boxCountTitle')}
          </CardTitle>
        </CardHeader>
        <CardContent className='space-y-4'>
          <div>
            <p className='text-muted-foreground text-sm'>{t('shipmentId')}</p>
            <p className='font-medium'>{shipmentNumber}</p>
          </div>

          <div className='space-y-2'>
            <div className='flex items-center justify-between'>
              <Label htmlFor='actual-box-count'>{t('actualBoxCount')}</Label>
              {/* <span className='text-muted-foreground text-sm'>
                {t('expectedBoxesLabel', { count: expectedBoxes })}
              </span> */}
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleVerify();
              }}
              className='flex gap-2'
            >
              <Input
                id='actual-box-count'
                type='number'
                min={0}
                value={boxCount}
                onChange={(e) => {
                  setBoxCount(e.target.value);
                  setCountVerified(false);
                  setCountStatus(null);
                  setDiscrepancyReported(false);
                }}
                placeholder={t('enterBoxCount')}
                disabled={countStatus === 'matched'}
              />
              <Button
                type='submit'
                disabled={
                  !boxCount.trim() ||
                  verifyCountMutation.isPending ||
                  countStatus === 'matched'
                }
              >
                {verifyCountMutation.isPending ? t('verifying') : t('verify')}
              </Button>
            </form>
          </div>

          {countVerified && countStatus === 'matched' && (
            <div
              className={cn(
                'flex items-center gap-2 rounded-md border border-green-200 bg-green-50 p-3 text-green-700',
                'dark:border-green-800 dark:bg-green-950 dark:text-green-300'
              )}
            >
              <CheckCircle2 className='h-5 w-5 shrink-0' />
              <span className='text-sm font-medium'>
                {t('countMatchedMessage', {
                  actual: boxCount,
                  expected: expectedBoxes
                })}
              </span>
            </div>
          )}

          {countVerified && countStatus === 'mismatched' && (
            <div className='space-y-4'>
              <div
                className={cn(
                  'flex items-center gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-red-700',
                  'dark:border-red-800 dark:bg-red-950 dark:text-red-300'
                )}
              >
                <AlertTriangle className='h-5 w-5 shrink-0' />
                <span className='text-sm font-medium'>
                  {t('countMismatchedMessage', {
                    actual: boxCount,
                    expected: expectedBoxes
                  })}
                </span>
              </div>

              {!discrepancyReported && (
                <div className='space-y-2'>
                  <Label htmlFor='discrepancy-notes'>
                    {t('discrepancyNotes')}
                  </Label>
                  <Input
                    id='discrepancy-notes'
                    value={discrepancyNotes}
                    onChange={(e) => setDiscrepancyNotes(e.target.value)}
                    placeholder={t('enterDiscrepancyNotes')}
                  />
                  <Button
                    variant='destructive'
                    onClick={handleReportDiscrepancy}
                    disabled={reportDiscrepancyMutation.isPending}
                  >
                    {reportDiscrepancyMutation.isPending
                      ? t('reporting')
                      : t('reportDiscrepancy')}
                  </Button>
                </div>
              )}

              {discrepancyReported && (
                <div
                  className={cn(
                    'flex items-center gap-2 rounded-md border border-yellow-200 bg-yellow-50 p-3 text-yellow-700',
                    'dark:border-yellow-800 dark:bg-yellow-950 dark:text-yellow-300'
                  )}
                >
                  <AlertTriangle className='h-5 w-5 shrink-0' />
                  <span className='text-sm font-medium'>
                    {t('discrepancyReportedMessage')}
                  </span>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <div className='flex justify-between'>
        <Button variant='outline' onClick={onBack}>
          <ArrowLeft className='me-2 h-4 w-4 rtl:rotate-180' />
          {t('back')}
        </Button>
        <Button onClick={onComplete} disabled={!canContinue}>
          {t('continueToDataVerification')}
        </Button>
      </div>
    </div>
  );
}
